import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axiosInstance';

// Async thunk to fetch stores
export const fetchStores = createAsyncThunk('store/fetchStores', async () => {
  const response = await axiosInstance.get('/store');
  return response.data;
});

// Async thunk to fetch owner stores
export const fetchOwnerStores = createAsyncThunk('store/fetchOwnerStores', async () => {
  const response = await axiosInstance.get('/store/owner');
  return response.data;
});

// Async thunk to create a new store
export const createStore = createAsyncThunk('store/createStore', async (storeData) => {
  const response = await axiosInstance.post('/store', storeData);
  return response.data;
});

// Async thunk to update store
export const updateStore = createAsyncThunk('store/updateStore', async ({ storeId, storeData }) => {
  const response = await axiosInstance.put(`/store/${storeId}`, storeData);
  return response.data;
});

// Async thunk to get store statistics
export const getStoreStats = createAsyncThunk('store/getStoreStats', async (storeId) => {
  const response = await axiosInstance.get(`/store/${storeId}/stats`);
  return response.data;
});

const storeSlice = createSlice({
  name: 'store',
  initialState: { 
    stores: [], 
    ownerStores: [],
    selectedStore: null,
    storeStats: {},
    status: 'idle', 
    error: null 
  },
  reducers: {
    setStore: (state, action) => {
      state.store = action.payload;
    },
    setSelectedStore: (state, action) => {
      // Check if we're actually changing stores
      const isStoreChange = !state.selectedStore || 
        (state.selectedStore._id !== action.payload?._id && 
         state.selectedStore.storeId !== action.payload?.storeId);
      
      state.selectedStore = action.payload;
      
      // Add a flag to indicate store change for other slices to listen to
      if (isStoreChange) {
        state.lastStoreChange = Date.now();
      }
    },
    clearStoreStats: (state) => {
      state.storeStats = {};
    },
    clearStoreData: (state) => {
      // This action will be used to clear data when switching stores
      state.lastStoreChange = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStores.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.stores = action.payload;
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchOwnerStores.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchOwnerStores.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.ownerStores = action.payload;
      })
      .addCase(fetchOwnerStores.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(createStore.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createStore.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.ownerStores.push(action.payload);
      })
      .addCase(createStore.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(updateStore.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateStore.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.ownerStores.findIndex(store => store.storeId === action.payload.storeId);
        if (index !== -1) {
          state.ownerStores[index] = action.payload;
        }
      })
      .addCase(updateStore.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(getStoreStats.fulfilled, (state, action) => {
        state.storeStats[action.payload.storeId] = action.payload;
      });
  },
});

export const { setStore, setSelectedStore, clearStoreStats, clearStoreData } = storeSlice.actions;
export default storeSlice.reducer;
